import { Flag, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Divider } from "@/components/misc";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { ProjectProps } from "@/interfaces/project_types";
import { ProjectProgress } from "../ProjectProgress/MainContainer";
import { NotesSection } from "../ProjectProgress/Utils/NotesSection";
import { CardDropdownProject } from "./Utils/CardDropdown";
import { CardBase, isRecentlyUpdated, timeAgo } from "./Utils/CardHelper";
import { TitleHeaderHyperlink } from "./Utils/TitleHeaderHyperlink";

type ProjectCardProps = {
	project: ProjectProps;
	isNew: boolean;
	onView: () => void;
	isMini: boolean;
};

export function ProjectCard({
	project,
	isNew,
	onView,
	isMini,
}: ProjectCardProps) {
	const [isNewLocal, setIsNewLocal] = useState(isNew);

	useEffect(() => {
		if (isNewLocal) {
			setIsNewLocal(true);
			onView();
		}
	}, [isNewLocal, onView]);

	return (
		<CardBase
			isNew={isNewLocal}
			hoverInsteadOfClick={isMini ? false : true} // Mini cards don't have hover children (can cause issues)
			hoverChildren={
				<CardDropdownProject
					edit_project_route={`/projects/update_project/${project.project_id}`}
					create_task_route={`/tasks/create_task_from_project/${project.project_id}`}
					assign_tasks_route={`/tasks/assign_tasks/${project.project_id}`}
					folder_path={`${project.folder_location}`}
					create_rfi_route={`/rfi/create_rfi_from_project/${project.project_id}`}
					create_submittal_route={`/submittals/create_submittal_from_project/${project.project_id}`}
				/>
			}
		>
			{isMini ? (
				<MiniProjectCard project={project} />
			) : (
				<DefaultProjectCard project={project} />
			)}
		</CardBase>
	);
}

function ProjectTitleWithAlert({ project }: { project: ProjectProps }) {
	const missing = project.missing_required_templates_count ?? 0;
	return (
		<div className="flex flex-row items-center gap-2">
			<TitleHeaderHyperlink
				title={project.project_name}
				folder_location={project.folder_location ?? ""}
			/>
			{project.updated_at && isRecentlyUpdated(project.updated_at) && (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<Flag
								className="w-4 h-4 text-green-500 dark:text-green-400 shrink-0"
								aria-label="Recently updated"
							/>
						</TooltipTrigger>
						<TooltipContent>
							<p className="text-sm">Updated {timeAgo(project.updated_at)}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			)}
			{missing > 0 && (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<TriangleAlert
								className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0"
								aria-label="Missing required tasks"
							/>
						</TooltipTrigger>
						<TooltipContent>
							<p className="text-sm w-64 text-center">
								{missing} required default{" "}
								{missing === 1 ? "task hasn't" : "tasks haven't"} been assigned
								yet.
							</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			)}
		</div>
	);
}

function MiniProjectCard({ project }: { project: ProjectProps }) {
	return <ProjectTitleWithAlert project={project} />;
}

function DefaultProjectCard({ project }: { project: ProjectProps }) {
	return (
		<>
			<ProjectTitleWithAlert project={project} />

			<Divider />

			<NotesSection notes={project.description ?? ""} />

			<div className="flex flex-row justify-between items-center">
				<ProjectProgress project={project} />
			</div>
			<p className="text-right text-sm">
				{project.end_date
					? `Next Deadline: ${project.end_date}`
					: "No Date Due"}
			</p>
		</>
	);
}

